import React, { useState, useEffect } from 'react';
import { getAuthToken } from '../../utils/authToken';
import { Link } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  PlusIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  MapPinIcon,
  UserIcon,
  EyeIcon,
  DocumentTextIcon,
  TagIcon,
  TrashIcon,
  EyeSlashIcon,
  PencilSquareIcon,
  ShieldCheckIcon,
  LockClosedIcon,
  ArrowTopRightOnSquareIcon,
  InboxArrowDownIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { formatCurrency } from '../../utils/currency';

const CallForProjects = () => {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  const categories = ['Technologie', 'Formation', 'Social', 'Environnement', 'Santé', 'Éducation', 'Culture'];
  const types = ['financement', 'formation', 'concours', 'partenariat', 'emploi'];

  useEffect(() => {
    fetchProjects();
  }, [searchTerm, selectedCategory, selectedType, selectedStatus, currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: 9
      });

      if (searchTerm) params.append('search', searchTerm);
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedType) params.append('type', selectedType);
      if (selectedStatus) params.append('status', selectedStatus);

      // Jeton transmis si connecté : le serveur détaille alors les appels réservés au Pro
      const jeton = await getAuthToken();
      const response = await fetch(`/api/call-for-projects?${params}`, jeton ? { headers: { Authorization: `Bearer ${jeton}` } } : undefined);
      
      if (response.ok) {
        const data = await response.json();
        setProjects(data.projects);
        setTotalPages(data.totalPages);
      } else {
        toast.error('Erreur lors du chargement des appels à projet');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };


  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getDaysRemaining = (deadline) => {
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'financement':
        return 'bg-green-100 text-green-800';
      case 'formation':
        return 'bg-blue-100 text-blue-800';
      case 'concours':
        return 'bg-purple-100 text-purple-800';
      case 'partenariat':
        return 'bg-orange-100 text-orange-800';
      case 'emploi':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'financement':
        return 'Financement';
      case 'formation':
        return 'Formation';
      case 'concours':
        return 'Concours';
      case 'partenariat':
        return 'Partenariat';
      case 'emploi':
        return 'Emploi';
      default:
        return type;
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSelectedType('');
    setSelectedStatus('');
    setCurrentPage(1);
  };

  const isAdmin = ['admin', 'super-admin'].includes(user?.role);

  const isManager = (project) => {
    if (!user) return false;
    return user.id === (project.owner_id || project.authorId) || isAdmin;
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Supprimer l'appel "${title}" ? Cette action est irréversible.`)) return;
    try {
      const token = await getAuthToken();
      const res = await fetch(`/api/call-for-projects/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error((await res.json()).message);
      toast.success('Appel supprimé');
      setProjects(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      toast.error(err.message || 'Erreur lors de la suppression');
    }
  };

  const handleToggleVisibility = async (project) => {
    const newVisible = !project.isVisible;
    try {
      const token = await getAuthToken();
      const res = await fetch(`/api/call-for-projects/${project.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ is_visible: newVisible })
      });
      if (!res.ok) throw new Error((await res.json()).message);
      toast.success(newVisible ? 'Appel rendu visible' : 'Appel masqué');
      setProjects(prev => prev.map(p => p.id === project.id ? { ...p, isVisible: newVisible } : p));
    } catch (err) {
      toast.error(err.message || 'Erreur');
    }
  };

  const handleToggleVerified = async (project) => {
    const newVerified = !project.isVerified;
    try {
      const token = await getAuthToken();
      const res = await fetch(`/api/call-for-projects/${project.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ is_verified: newVerified })
      });
      if (!res.ok) throw new Error((await res.json()).message);
      toast.success(newVerified ? 'Badge "Vérifié" attribué' : 'Badge retiré');
      setProjects(prev => prev.map(p => p.id === project.id ? { ...p, isVerified: newVerified } : p));
    } catch (err) {
      toast.error(err.message || 'Erreur');
    }
  };

  const handleTogglePublishedByInnovactive = async (project) => {
    const newVal = !project.isPublishedByInnovactive;
    try {
      const token = await getAuthToken();
      const res = await fetch(`/api/call-for-projects/${project.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ is_published_by_innovactive: newVal })
      });
      if (!res.ok) throw new Error((await res.json()).message);
      toast.success(newVal ? 'Badge "Publié par Innovactive360" attribué' : 'Badge retiré');
      setProjects(prev => prev.map(p => p.id === project.id
        ? { ...p, isPublishedByInnovactive: newVal, receivesApplications: newVal || p.applicationsOnPlatform === true }
        : p));
    } catch (err) {
      toast.error(err.message || 'Erreur');
    }
  };

  // Candidatures reçues sur Innovactive360 (appel sponsorisé par un partenaire qui l'autorise).
  const handleToggleApplicationsOnPlatform = async (project) => {
    const newVal = !project.applicationsOnPlatform;
    try {
      const token = await getAuthToken();
      const res = await fetch(`/api/call-for-projects/${project.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ applications_on_platform: newVal })
      });
      if (!res.ok) throw new Error((await res.json()).message);
      toast.success(newVal
        ? 'Les candidatures seront reçues sur Innovactive360'
        : "Les candidatures se feront sur le site de l'organisateur");
      setProjects(prev => prev.map(p => p.id === project.id
        ? { ...p, applicationsOnPlatform: newVal, receivesApplications: newVal || p.isPublishedByInnovactive === true }
        : p));
    } catch (err) {
      toast.error(err.message || 'Erreur');
    }
  };

  const handleToggleApplyButton = async (project) => {
    const newVal = !project.showApplyButton;
    try {
      const token = await getAuthToken();
      const res = await fetch(`/api/call-for-projects/${project.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ show_apply_button: newVal })
      });
      if (!res.ok) throw new Error((await res.json()).message);
      toast.success(newVal ? 'Bouton "Candidater" activé' : 'Bouton "Candidater" masqué');
      setProjects(prev => prev.map(p => p.id === project.id ? { ...p, showApplyButton: newVal } : p));
    } catch (err) {
      toast.error(err.message || 'Erreur');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* En-tête */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Appels à Projet</h1>
              <p className="mt-2 text-gray-600">
                Découvrez les opportunités de financement, formation et partenariat
              </p>
            </div>
            {(user?.role === 'admin' || user?.role === 'super-admin') && (
              <Link
                to="/call-for-projects/create"
                className="bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 flex items-center gap-2"
              >
                <PlusIcon className="h-5 w-5" />
                Publier un appel
              </Link>
            )}
          </div>
        </div>

        {/* Barre de recherche et filtres */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Recherche */}
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher un appel à projet..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            {/* Bouton filtres */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <FunnelIcon className="h-5 w-5" />
              Filtres
            </button>
          </div>

          {/* Filtres avancés */}
          {showFilters && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Catégorie
                  </label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Toutes les catégories</option>
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type
                  </label>
                  <select
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Tous les types</option>
                    {types.map(type => (
                      <option key={type} value={type}>{getTypeLabel(type)}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Statut
                  </label>
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Tous les statuts</option>
                    <option value="active">Actif</option>
                    <option value="upcoming">À venir</option>
                    <option value="closed">Fermé</option>
                  </select>
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  onClick={clearFilters}
                  className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                >
                  Effacer les filtres
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Liste des appels à projet */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-12">
            <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun appel à projet trouvé</h3>
            <p className="text-gray-500">Essayez de modifier vos critères de recherche</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {projects.map((project) => {
                const daysRemaining = getDaysRemaining(project.deadline);
                const isUrgent = daysRemaining <= 7 && daysRemaining > 0;
                const isExpired = daysRemaining <= 0;

                return (
                  <div key={project.id} className={`bg-white rounded-lg shadow hover:shadow-lg transition-shadow ${!project.isVisible ? 'opacity-60 ring-1 ring-yellow-300' : ''}`}>
                    <div className="p-6">
                      {/* En-tête de la carte */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(project.type)}`}>
                              {getTypeLabel(project.type)}
                            </span>
                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                              {project.category}
                            </span>
                            {project.isVerified && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                <ShieldCheckIcon className="h-3 w-3" />
                                Vérifié
                              </span>
                            )}
                            {project.isPublishedByInnovactive && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800">
                                <ShieldCheckIcon className="h-3 w-3" />
                                Publié par Innovactive360
                              </span>
                            )}
                            {project.acces === 'pro' && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-800">
                                <LockClosedIcon className="h-3 w-3" />
                                Réservé Pro
                              </span>
                            )}
                            {!project.isVisible && isManager(project) && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                <EyeSlashIcon className="h-3 w-3" />
                                Masqué
                              </span>
                            )}
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
                            {project.title}
                          </h3>
                        </div>
                      </div>

                      {/* Description */}
                      <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                        {project.reserve
                          ? 'Appel réservé aux abonnés Pro et Pro Max : détails et candidature avec l\'abonnement.'
                          : project.description}
                      </p>

                      {/* Informations clés */}
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center text-sm text-gray-600">
                          <CurrencyDollarIcon className="h-4 w-4 mr-2" />
                          <span className="font-medium">{formatCurrency(project.budget, project.currency)}</span>
                        </div>
                        
                        <div className="flex items-center text-sm text-gray-600">
                          <MapPinIcon className="h-4 w-4 mr-2" />
                          <span>{project.location?.country || 'Non spécifié'}</span>
                        </div>
                        
                        <div className="flex items-center text-sm text-gray-600">
                          <UserIcon className="h-4 w-4 mr-2" />
                          <span>{project.owner?.firstName} {project.owner?.lastName}</span>
                        </div>
                      </div>

                      {/* Date limite */}
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center text-sm">
                          <CalendarIcon className="h-4 w-4 mr-2 text-gray-400" />
                          <span className={isExpired ? 'text-red-600' : isUrgent ? 'text-orange-600' : 'text-gray-600'}>
                            {isExpired ? 'Expiré' : `Expire le ${formatDate(project.deadline)}`}
                          </span>
                        </div>
                        {!isExpired && (
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            isUrgent ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {daysRemaining} jour{daysRemaining > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>

                      {/* Statistiques */}
                      <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                        <div className="flex items-center">
                          <EyeIcon className="h-4 w-4 mr-1" />
                          <span>{project.viewsCount} vues</span>
                        </div>
                        <div className="flex items-center">
                          <DocumentTextIcon className="h-4 w-4 mr-1" />
                          <span>{project.applicationsCount} candidatures</span>
                        </div>
                      </div>

                      {/* Tags */}
                      {project.tags && project.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-4">
                          {project.tags.slice(0, 3).map((tag, index) => (
                            <span key={index} className="inline-flex items-center px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                              <TagIcon className="h-3 w-3 mr-1" />
                              {tag}
                            </span>
                          ))}
                          {project.tags.length > 3 && (
                            <span className="text-xs text-gray-400">+{project.tags.length - 3}</span>
                          )}
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex gap-2">
                        <Link
                          to={`/call-for-projects/${project.id}`}
                          className="flex-1 bg-primary-600 text-white text-center py-2 px-4 rounded-lg hover:bg-primary-700 transition-colors"
                        >
                          Voir les détails
                        </Link>
                        {!isExpired && !isManager(project) && project.showApplyButton !== false && (
                          project.receivesApplications ? (user && (
                            <Link
                              to={`/call-for-projects/${project.id}/apply`}
                              className="bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors"
                            >
                              Candidater
                            </Link>
                          )) : project.applicationUrl && (
                            // Appel d'un autre organisme : candidature sur son site.
                            <a
                              href={project.applicationUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Candidater sur le site de l'organisateur"
                              className="inline-flex items-center gap-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors"
                            >
                              Candidater
                              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                            </a>
                          )
                        )}
                        {isManager(project) && (
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleToggleVisibility(project)}
                              className={`p-2 rounded-lg transition-colors ${project.isVisible ? 'text-green-600 bg-green-50 hover:bg-green-100' : 'text-yellow-600 bg-yellow-50 hover:bg-yellow-100'}`}
                              title={project.isVisible ? 'Masquer' : 'Rendre visible'}
                            >
                              {project.isVisible ? <EyeIcon className="h-5 w-5" /> : <EyeSlashIcon className="h-5 w-5" />}
                            </button>
                            {isAdmin && (
                              <>
                                <button
                                  onClick={() => handleToggleVerified(project)}
                                  className={`p-2 rounded-lg transition-colors ${project.isVerified ? 'text-blue-600 bg-blue-50 hover:bg-blue-100' : 'text-gray-400 bg-gray-50 hover:bg-gray-100'}`}
                                  title={project.isVerified ? 'Retirer badge Vérifié' : 'Marquer Vérifié'}
                                >
                                  <ShieldCheckIcon className="h-5 w-5" />
                                </button>
                                <button
                                  onClick={() => handleTogglePublishedByInnovactive(project)}
                                  className={`p-2 rounded-lg transition-colors text-xs font-bold ${project.isPublishedByInnovactive ? 'text-indigo-600 bg-indigo-50 hover:bg-indigo-100' : 'text-gray-400 bg-gray-50 hover:bg-gray-100'}`}
                                  title={project.isPublishedByInnovactive ? 'Retirer badge "Publié par Innovactive360"' : 'Marquer "Publié par Innovactive360"'}
                                >
                                  <span className="text-xs">I360</span>
                                </button>
                                <button
                                  onClick={() => handleToggleApplicationsOnPlatform(project)}
                                  className={`p-2 rounded-lg transition-colors ${project.applicationsOnPlatform ? 'text-indigo-600 bg-indigo-50 hover:bg-indigo-100' : 'text-gray-400 bg-gray-50 hover:bg-gray-100'}`}
                                  title={project.applicationsOnPlatform
                                    ? "Candidatures reçues sur Innovactive360 (partenaire). Cliquer pour renvoyer vers le site de l'organisateur"
                                    : "Candidatures sur le site de l'organisateur. Cliquer si le partenaire autorise leur réception sur Innovactive360"}
                                  aria-label="Où se font les candidatures"
                                >
                                  <InboxArrowDownIcon className="h-5 w-5" />
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => handleToggleApplyButton(project)}
                              className={`p-2 rounded-lg transition-colors text-xs ${project.showApplyButton !== false ? 'text-green-600 bg-green-50 hover:bg-green-100' : 'text-red-600 bg-red-50 hover:bg-red-100'}`}
                              title={project.showApplyButton !== false ? 'Masquer le bouton Candidater' : 'Afficher le bouton Candidater'}
                            >
                              {project.showApplyButton !== false ? '✓C' : '✗C'}
                            </button>
                            <Link
                              to={`/call-for-projects/${project.id}/edit`}
                              className="p-2 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                              title="Modifier"
                            >
                              <PencilSquareIcon className="h-5 w-5" />
                            </Link>
                            <button
                              onClick={() => handleDelete(project.id, project.title)}
                              className="p-2 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                              title="Supprimer"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center">
                <nav className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Précédent
                  </button>
                  
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-2 text-sm font-medium rounded-lg ${
                        page === currentPage
                          ? 'bg-primary-600 text-white'
                          : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Suivant
                  </button>
                </nav>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CallForProjects;

